import { useMemo } from 'react'
import { useHealthChecksByTenant } from '@/hooks/useHealthChecks'
import { Badge } from '@/components/ui/Badge'
import { Skeleton } from '@/components/ui/Skeleton'
import { HealthBadge } from './HealthBadge'
import { formatRelativeTime } from '@/lib/utils'
import type { THealthCheck, OverallHealthStatus } from '@/types'

interface HealthTimelineProps {
  tenantId: string
}

const STATUS_BAND_COLORS: Record<OverallHealthStatus, string> = {
  healthy: 'var(--status-healthy)',
  degraded: 'var(--status-warning)',
  unhealthy: 'var(--status-unhealthy)',
  unreachable: 'var(--status-unknown, var(--text-tertiary))',
}

const STATUS_BADGE_VARIANT: Record<OverallHealthStatus, 'success' | 'warning' | 'error' | 'default'> = {
  healthy: 'success',
  degraded: 'warning',
  unhealthy: 'error',
  unreachable: 'default',
}

export function HealthTimeline({ tenantId }: HealthTimelineProps) {
  const { data: healthChecks = [], isLoading } = useHealthChecksByTenant(tenantId)

  const last24h = useMemo(() => {
    const cutoff = Date.now() - 24 * 60 * 60 * 1000
    return healthChecks.filter(hc => new Date(hc.checked_at).getTime() >= cutoff)
  }, [healthChecks])

  const latest = healthChecks[0] as THealthCheck | undefined

  if (isLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Skeleton height={32} />
        <Skeleton height={80} />
      </div>
    )
  }

  if (!latest) {
    return (
      <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '13px' }}>
        No health data available for the last 24 hours.
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {last24h.length > 1 && (
        <div>
          <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '8px', display: 'block' }}>Last 24 Hours</span>
          <div style={{ display: 'flex', height: '12px', borderRadius: 'var(--radius-sm)', overflow: 'hidden', backgroundColor: 'var(--bg-tertiary)' }}>
            {last24h.map((hc, i) => (
              <div key={hc.id ?? i} style={{ flex: 1, backgroundColor: STATUS_BAND_COLORS[hc.overall_status] ?? 'var(--text-tertiary)', borderRight: i < last24h.length - 1 ? '1px solid var(--bg-secondary)' : undefined }} title={`${hc.overall_status} at ${new Date(hc.checked_at).toLocaleTimeString()}`} />
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
            <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>24h ago</span>
            <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>now</span>
          </div>
        </div>
      )}

      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <HealthBadge status={latest.overall_status} size={10} />
          <Badge variant={STATUS_BADGE_VARIANT[latest.overall_status]}>{latest.overall_status}</Badge>
          <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{formatRelativeTime(latest.checked_at)}</span>
        </div>

        {latest.services && (
          <div style={{ marginBottom: '16px' }}>
            <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '8px', display: 'block' }}>Services</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              {(['caddy', 'pocketbase', 'frontend', 'action_runner', 'openclaw', 'n8n'] as const).map(service => {
                const check = latest.services?.[service]
                if (!check) return null  // n8n is absent on tenants without n8n provisioned
                const isUp = check.status === 'up'
                return (
                  <div key={service} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: isUp ? 'var(--status-healthy)' : 'var(--status-unhealthy)', flexShrink: 0 }} />
                    <span>{service.replace('_', ' ')}</span>
                    {check.response_ms != null && <span style={{ fontSize: '10px', fontFamily: 'var(--font-mono)', color: 'var(--text-tertiary)' }}>{check.response_ms}ms</span>}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {latest.resource_usage && (latest.resource_usage.cpu_percent != null || latest.resource_usage.memory_percent != null || latest.resource_usage.disk_percent != null) && (
          <div>
            <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '8px', display: 'block' }}>Resources</span>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              {(['cpu_percent', 'memory_percent', 'disk_percent'] as const).map(key => {
                const value = latest.resource_usage?.[key] ?? 0
                const label = key.replace('_percent', '').toUpperCase()
                const color = value >= 90 ? 'var(--status-unhealthy)' : value >= 70 ? 'var(--status-warning)' : 'var(--status-healthy)'
                return (
                  <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{label}</span>
                      <span style={{ fontSize: '11px', fontFamily: 'var(--font-mono)', color }}>{Math.round(value)}%</span>
                    </div>
                    <div style={{ height: '4px', backgroundColor: 'var(--bg-tertiary)', borderRadius: 'var(--radius-full)', overflow: 'hidden' }}>
                      <div style={{ width: `${Math.min(100, Math.max(0, value))}%`, height: '100%', backgroundColor: color, borderRadius: 'var(--radius-full)', transition: 'width 300ms ease' }} />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
